import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Spinner,
  Icon,
} from '@chakra-ui/react';
import { FaCreditCard } from 'react-icons/fa';

import { getAuthHeaders } from '../utils/apiAuth';
function UpdatePaymentPage() {
  const { labId } = useParams();
  const [labName, setLabName] = useState('');
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Fetch lab name for branding
    const fetchLab = async () => {
      try {
        const { createClient } = await import('@supabase/supabase-js');
        const supabase = createClient(
          process.env.REACT_APP_SUPABASE_URL,
          process.env.REACT_APP_SUPABASE_ANON_KEY
        );
        const { data } = await supabase
          .from('labs')
          .select('name')
          .eq('id', labId)
          .single();
        if (data?.name) setLabName(data.name);
      } catch {
        // Lab name is optional — page still works without it
      } finally {
        setLoading(false);
      }
    };
    if (labId) fetchLab();
    else setLoading(false);
  }, [labId]);

  const handleUpdatePayment = async () => {
    setRedirecting(true);
    setError('');
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${apiBase}/api/send-payment-update-link`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ labId, sendEmail: false }),
      });
      const data = await res.json().catch(() => null);
      if (!data || !data.url) {
        throw new Error(data?.error || 'Unable to create payment session');
      }
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'Something went wrong. Please contact support.');
      setRedirecting(false);
    }
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box bg="linear-gradient(135deg, #0066cc 0%, #004499 100%)" bgGradient="linear(to-br, blue.600, blue.800)" py={6} px={4} textAlign="center">
        <Heading size="lg" color="white">AtHomeBloodDraw.com</Heading>
        <Text color="blue.100" fontSize="sm" mt={1}>Secure Payment Portal</Text>
      </Box>

      {/* Content */}
      <Box display="flex" alignItems="center" justifyContent="center" p={4} mt={8}>
        <Box bg="white" p={8} rounded="xl" shadow="lg" maxW="480px" w="full" textAlign="center">
          <VStack spacing={5}>
            <Box bg="blue.50" p={4} rounded="full">
              <Icon as={FaCreditCard} boxSize={10} color="blue.500" />
            </Box>

            <Heading size="md" color="gray.800">
              Update Payment Method
            </Heading>

            {labName && (
              <Text color="gray.500" fontSize="sm">
                For: <strong>{labName}</strong>
              </Text>
            )}

            <Text color="gray.600" fontSize="md" lineHeight="1.7">
              Click the button below to securely add or update your credit/debit card.
              You'll be redirected to a secure page powered by Stripe.
            </Text>

            <Text color="gray.400" fontSize="xs">
              We never see or store your card details directly.
            </Text>

            {error && (
              <Box bg="red.50" p={3} rounded="md" w="full">
                <Text color="red.600" fontSize="sm">{error}</Text>
              </Box>
            )}

            <Button
              colorScheme="blue"
              size="lg"
              w="full"
              leftIcon={<Icon as={FaCreditCard} />}
              onClick={handleUpdatePayment}
              isLoading={redirecting}
              loadingText="Redirecting to Stripe..."
              mt={2}
            >
              Update Payment Method
            </Button>
          </VStack>

          {/* Footer */}
          <Box mt={8} pt={4} borderTopWidth="1px" borderColor="gray.200">
            <Text fontSize="xs" color="gray.400">
              AtHomeBloodDraw.com | +1 844-718-7800 | contact@athomeblooddraw.com
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default UpdatePaymentPage;
